// Libs
import React, { useCallback, useEffect, useState } from 'react';
import { Collapse, Icon, Tooltip, Modal } from "antd";
import { useRecoilState, useRecoilValue } from 'recoil';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';

// Own Libs
import { Button, ColumnProps, Table, Widget } from '@digital/ui';

//Api
import {
    deleteDocumentosConsolidacaoAnexosFinais,
    downloadArquivo,
    downloadArquivosOrdemCompra,
    getDocumentosConsolidacaoAnexosFinais,
    incluirDocumento,
    ISasDocumentoOrdemCompra,
    substituirSasArquivoDocumentoOrdemCompra,
    updateDocumentosConsolidacaoAnexosFinais,
    updateListSasDocumentoOrdemCompraCommand,
    viewArquivo
} from '@app/api/sas/sasDocumentoOrdemCompra';

//Files
import { dadosIdentificacaoState, ordemCompraIdState, reloadAnexosState } from '../atoms';
import { ModalInclusaoMultAnexo, Space, Visibility } from '@app/components';
import { componentsSorting } from '@app/components/SortingTable';

//Utils
import { compareNumber } from '@app/helpers/comparators';
import { GetFileExtension, imageFormats } from '@app/helpers';
import { hasPermission } from '@app/helpers/permissions';
import { EGuidTipoSasDocumento } from '@app/enums/EGuidTipoSasDocumento';
import { baixarTemplateCartaConvite } from '@app/api/template';
import { getPropostasOrdemCompra } from '@app/api/sas/sasProposta';
import UploadModalSubstituirAnexo from '@app/components/Upload/uploadModalSubstituirAnexo';
import { isObjEmpty, isObjNotEmpty } from '@app/helpers/objHelper';
import { downloadOrViewArquivo } from '@app/helpers/download';

const WidgetConsolidacaoAnexosFinais = () => {
    const ordemCompraId = useRecoilValue(ordemCompraIdState);
    const dadosIdentificacaoValue = useRecoilValue(dadosIdentificacaoState);
    const [reloadAnexos, setReloadAnexos] = useRecoilState(reloadAnexosState);

    const [arquivos, setArquivos] = useState<ISasDocumentoOrdemCompra[]>([]);
    const [propostaVencedoraId, setPropostaVencedoraId] = useState();

    const onUpdateAnexos = () => {
        setReloadAnexos(!reloadAnexos);
    };

    useEffect(() => {
        setArquivos([]);

        getPropostasOrdemCompra({
            ordemCompraId: ordemCompraId || ""
        }).then((result: any) => {
            if (result) {
                const pv = result.find(f => f.numeroVencedor == 1);
                if (pv) {
                    setPropostaVencedoraId(pv.id);

                    getDocumentosConsolidacaoAnexosFinais({
                        ordemCompraId
                    }).then((docs1) => {
                        setArquivos(docs1.ativos);
                    });
                }
            }
        });
    }, [reloadAnexos]);


    const columns = () => {
        let columns: ColumnProps<any>[] = [];

        columns.push({
            title: 'Número Anexo',
            dataIndex: 'ordem',
            width: '5%',
            align: 'center',
            render: (index: number, record: any) => <span>{record.ordem}</span>
        });

        columns.push({
            title: 'Arquivo',
            width: '30%',
            render: (index: number, record: any) => <span>{record.arquivoNome || record.arquivoNomeDownload || record.descricao}</span>
        });

        columns.push({
            title: 'Ações',
            dataIndex: '',
            className: 'td-inicio',
            width: '5%',
            render(text, record, index) {
                return <>
                    {/* <ModalInclusaoMultAnexo
                        visible={isObjNotEmpty(record.children)}
                        icon="editing-pencil"
                        title='Editar Descrição'
                        onSubmit={async (values) => {
                            return updateSasDocumentoOrdemCompraCommand(
                                {
                                    sasDocumentoOrdemCompraId: record.id,
                                    sasStatusDocumentoOrdemCompraId: record.sasStatusDocumentoOrdemCompraId,
                                    descricao: values.justificativa,
                                    ehDescricaoAlternativa: true
                                }
                            ).then(data => {
                                if (data) onUpdateAnexos()
                                return true;
                            })
                        }
                        }
                        showJustificativa
                        isJustificativaInput
                        labelJustificativa="Descrição do Documento"
                        placeholderJustificativa="Descrição do Documento"
                        textButtonSave="Salvar"
                        isMultAnexo={false}
                        showUploadArquivos={false}
                        initialValues={{
                            justificativa: record.descricao
                        }}
                    /> */}

                    <ModalInclusaoMultAnexo
                        buttonIcon="editing-pencil"
                        buttonTooltip='Editar nome arquivo'
                        buttonType='link'
                        onSubmit={async (values) => {
                            return updateDocumentosConsolidacaoAnexosFinais(
                                record.arquivoId,
                                values.justificativa
                            ).finally(() => {
                                onUpdateAnexos();
                                return true;
                            });
                        }}
                        showJustificativa
                        isJustificativaInput
                        labelJustificativa="Nome arquivo"
                        placeholderJustificativa="Nome arquivo"
                        textButtonSave="Salvar"
                        isMultAnexo={false}
                        showUploadArquivos={false}
                        initialValues={{
                            justificativa: record.arquivoNome
                        }}
                    />

                    <Visibility visible={isObjEmpty(record.children)} >
                        <UploadModalSubstituirAnexo
                            hasJustificativa
                            label=''
                            hasData={false}
                            hasFile={false}
                            onSubmit={async (values) => {
                                return substituirSasArquivoDocumentoOrdemCompra({
                                    description: values.justificativa,
                                    file: values.arquivo,
                                    sasArquivoDocumentoOrdemCompraId: record.arquivoId,
                                    ehArquivoConsolidacaoAnexosFinais: true
                                }).then((data: any) => {
                                    if (data && data.valid) {
                                        onUpdateAnexos();
                                        return true;
                                    }
                                    return false;
                                });
                            }}

                        />
                    </Visibility>

                    <Visibility
                        visible={isObjNotEmpty(record.arquivoId)}
                    >
                        <Tooltip title="Download Arquivo">
                            <Button
                                icon="ui-download"
                                type="link"
                                onClick={() => {
                                    downloadArquivo(
                                        record.arquivoId,
                                        record.arquivoNome
                                    );
                                }}
                            />
                        </Tooltip>
                    </Visibility>

                    <Visibility
                        visible={isObjEmpty(record.children) && (
                            GetFileExtension(record.arquivoNome) === "pdf" ||
                            imageFormats.includes(
                                GetFileExtension(record.arquivoNomeDownload)
                            ))
                        }
                    >
                        <Tooltip title="Visualizar Arquivo">
                            <Button
                                icon="files-view_file"
                                type="link"
                                onClick={() => {
                                    viewArquivo(
                                        record.arquivoId,
                                        record.arquivoNome
                                    );
                                }}
                            />
                        </Tooltip>
                    </Visibility>

                    <Visibility
                        permissions={["SAS_ORDENS_COMPRA_SUBSTITUIR_ANEXO"]}
                    >
                        <Tooltip title="Deletar Anexos">
                            <Button
                                onClick={() => {
                                    Modal.confirm({
                                        title: 'Deletar Item',
                                        icon: <Icon type="info-circle" />,
                                        content: 'Deseja excluir todos esses registros, incluindo todo seu histórico?',
                                        onOk() {
                                            deleteDocumentosConsolidacaoAnexosFinais(
                                                record.id,
                                                record.pai ? undefined : record.arquivoId
                                            )
                                                .then(data => {
                                                    if (data) {
                                                        onUpdateAnexos();
                                                    }
                                                });
                                        },
                                    });
                                }}
                                icon="commons-trash"
                            />
                        </Tooltip>
                    </Visibility>

                    <>
                        {!record.pai && (record.tipoDocumentoOrdemCompraId && [EGuidTipoSasDocumento.CartaConvite].includes(record.tipoDocumentoOrdemCompraId)) ? (
                            <Tooltip title="Baixar Template">
                                <Button
                                    icon="business-new_resume_template"
                                    onClick={() => baixarTemplateCartaConvite()}
                                />
                            </Tooltip>
                        ) : (
                            <></>
                        )}
                    </>
                </>;
            },
        });

        return columns;
    };


    const moveRow = useCallback(
        (dragIndex, hoverIndex) => {
            const dragRow = arquivos[dragIndex];
            const respUpdate = update(arquivos, {
                $splice: [
                    [dragIndex, 1],
                    [hoverIndex, 0, dragRow],
                ],
            });

            const ordem = respUpdate.map((ru, index) => {
                return { ...ru, ordem: index, descricao: respUpdate[index].descricao, arquivoNomeDownload: respUpdate[index].arquivoNomeDownload };
            });

            updateListSasDocumentoOrdemCompraCommand(ordem)
                .then((data: any) => {
                    setArquivos([]);
                    if (data && data.valid) {
                        setArquivos(data.sasDocumentoOrdemCompras);
                    }
                });
        },
        [arquivos],
    );

    return (
        <Collapse bordered={false}
            defaultActiveKey='WidgetConsolidacaoAnexosFinais'
            activeKey='WidgetConsolidacaoAnexosFinais' >
            <Collapse.Panel key='WidgetConsolidacaoAnexosFinais' header="Consolidação Anexos Finais" showArrow={false} >
                <Widget actions={[
                    <Button
                        type='primary'
                        onClick={() => {
                            downloadOrViewArquivo({
                                url: `/api/sasDocumentoOrdemCompra/DocumentosConsolidacaoAnexosFinais/download?ordemCompraId=${ordemCompraId}`,
                                nomeDoArquivo: `documentacao_oc_${dadosIdentificacaoValue.numero}.zip`,
                                atributoId: "downloadArquivosOrdemCompra"
                            });
                        }}
                    >Baixar Tudo
                    </Button>
                ]} >
                    <Visibility
                        visible={hasPermission(["SAS_ORDENS_COMPRA_SUBSTITUIR_ANEXO"])}
                    >
                        <ModalInclusaoMultAnexo
                            buttonIcon="ui-upload"
                            buttonTooltip='Incluir Anexo'
                            buttonLabel='Incluir Anexo'
                            onSubmit={async (values) =>
                                await incluirDocumento({
                                    ordemCompraId: ordemCompraId,
                                    descricao: values.justificativa,
                                    file: values.arquivo,
                                    ehArquivoConsolidacaoAnexosFinais: true
                                }).then((data: any) => {
                                    if (data && data.valid) {
                                        onUpdateAnexos();
                                        return true;
                                    }
                                    return false;
                                })
                            }
                            showJustificativa
                            isJustificativaInput
                            labelJustificativa="Descrição do Documento"
                            placeholderJustificativa="Descrição do Documento"
                            textButtonSave="Salvar"
                            isMultAnexo={false}
                        />
                        <Space mb={16} />
                    </Visibility>

                    <DndProvider backend={HTML5Backend}>
                        <Table
                            dataSource={arquivos.sort((a, b) => compareNumber(a.ordem, b.ordem))}
                            columns={columns()}
                            rowKey="id"
                            pagination={false}
                            components={componentsSorting}
                            onRow={(record, index) => ({
                                index,
                                moveRow,
                            })}
                        />
                    </DndProvider>
                </Widget>
            </Collapse.Panel>
        </Collapse>
    );
};

export default WidgetConsolidacaoAnexosFinais;